<template>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" height="38.4mm" width="38.4mm">
        <defs>
            <!-- Radial gradient for sun body: center bright → edge warm -->
            <radialGradient :id="uid + '-body'" cx="50%" cy="50%" r="50%">
                <stop offset="0%"   :stop-color="coreColor"  stop-opacity="1" />
                <stop offset="65%"  :stop-color="midColor"   stop-opacity="0.95" />
                <stop offset="100%" :stop-color="edgeColor"  stop-opacity="0.80" />
            </radialGradient>
            <!-- Outer halo glow: fades outward -->
            <radialGradient :id="uid + '-halo'" cx="50%" cy="50%" r="50%">
                <stop offset="55%" :stop-color="glowColor" stop-opacity="0" />
                <stop offset="100%" :stop-color="glowColor" :stop-opacity="glowOpacity" />
            </radialGradient>
            <!-- Directional overlay: bright on azimuth side, dark on opposite -->
            <linearGradient :id="uid + '-dir'"
                :x1="dirX1" :y1="dirY1" :x2="dirX2" :y2="dirY2">
                <stop offset="0%"   stop-color="white" :stop-opacity="dirBright" />
                <stop offset="55%"  stop-color="white" stop-opacity="0" />
                <stop offset="100%" stop-color="black" :stop-opacity="dirDark" />
            </linearGradient>
        </defs>

        <!-- Halo (only when sun is above horizon) -->
        <ellipse v-if="altitude > 0" cx="16" cy="16" rx="15" ry="15"
            :fill="`url(#${uid}-halo)`" />

        <!-- Sun body -->
        <ellipse cx="16" cy="16" rx="12" ry="12"
            :fill="`url(#${uid}-body)`"
            :opacity="bodyOpacity" />

        <!-- Directional highlight overlay -->
        <ellipse v-if="altitude > 0" cx="16" cy="16" rx="12" ry="12"
            :fill="`url(#${uid}-dir)`"
            :opacity="bodyOpacity" />

        <!-- Stroke -->
        <ellipse cx="16" cy="16" rx="12" ry="12"
            fill="none"
            :stroke="strokeColor"
            stroke-width="1"
            :stroke-opacity="bodyOpacity * 0.55" />
    </svg>
</template>

<script>
let _uid = 0

export default {
    name: 'SunGraph',
    props: {
        altitude: { type: Number, default: null },
        azimuth:  { type: Number, default: null },
    },
    data() {
        return { uid: 'sg-' + (++_uid) }
    },
    computed: {
        // 0 at horizon, 1 at zenith; clamped; works for negatives too
        altFrac() {
            if (this.altitude === null) return 0.5
            return Math.max(0, Math.min(1, this.altitude / 90))
        },
        bodyOpacity() {
            if (this.altitude === null) return 0.75
            if (this.altitude <= 0) {
                // Below horizon: faint glow, fades as sun goes deeper
                return Math.max(0.08, 0.22 + this.altitude / 18 * 0.14)
            }
            return 0.25 + this.altFrac * 0.75
        },
        // Core bright center: orange at horizon → near-white at zenith
        coreColor() {
            const r = 255
            const g = Math.round(165 + this.altFrac * 80)   // 165 → 245
            const b = Math.round(this.altFrac * 120)          //   0 → 120
            return `rgb(${r},${g},${b})`
        },
        midColor() {
            const g = Math.round(130 + this.altFrac * 80)
            return `rgb(255,${g},0)`
        },
        edgeColor() {
            const g = Math.round(100 + this.altFrac * 60)
            return `rgb(255,${g},0)`
        },
        glowColor() {
            const g = Math.round(150 + this.altFrac * 60)
            return `rgb(255,${g},20)`
        },
        glowOpacity() {
            return this.altFrac * 0.40
        },
        strokeColor() {
            const g = Math.round(80 + this.altFrac * 70)
            return `rgb(220,${g},0)`
        },
        // Directional gradient: lit side → azimuth direction, dark side → opposite
        _azRad() {
            return ((this.azimuth ?? 180) * Math.PI) / 180
        },
        dirX1() { return `${50 + 50 * Math.sin(this._azRad)}%` },
        dirY1() { return `${50 - 50 * Math.cos(this._azRad)}%` },
        dirX2() { return `${50 - 50 * Math.sin(this._azRad)}%` },
        dirY2() { return `${50 + 50 * Math.cos(this._azRad)}%` },
        // More directional contrast at low altitudes (raking light), less at zenith
        dirBright() { return 0.15 + (1 - this.altFrac) * 0.25 },
        dirDark()   { return 0.08 + (1 - this.altFrac) * 0.12 },
    },
}
</script>
